// Apolo IDE — usePlanSuggestions (sprint plan-suggestions).
//
// Híbrido: heurística local instantânea (computeSuggestions) + "✨ mais ideias"
// que chama o LLM (/api/chat) pra sugestões contextuais. Sem rota nova.

import { useCallback, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { computeSuggestions } from '../lib/plan/suggestions.js';

const MODEL = 'anthropic/claude-opus-4-7-medium';

function summarize(blocks) {
  if (!blocks?.length) return '(canvas vazio)';
  return blocks
    .map((b) => {
      const p = b.payload || {};
      const s = String(p.body || p.text || p.title || p.skill_name || p.label || '').slice(0, 120);
      return `- [${b.kind}] ${s}`;
    })
    .join('\n')
    .slice(0, 6000);
}

function parseAiSuggestions(content) {
  const fence = /```json\s*([\s\S]+?)```/.exec(content || '');
  const raw = fence ? fence[1] : content;
  const out = [];
  try {
    const arr = JSON.parse(String(raw).trim());
    if (Array.isArray(arr)) {
      for (const x of arr) {
        if (x && x.label && x.kind) {
          out.push({
            id: `ai-${out.length}-${String(x.label).slice(0, 16)}`,
            label: String(x.label).slice(0, 40),
            icon: 'Sparkles',
            reason: x.reason ? String(x.reason) : 'Sugestão da IA com base no seu canvas.',
            ai: true,
            actions: [{ kind: x.kind, payload: x.payload && typeof x.payload === 'object' ? x.payload : {} }],
          });
        }
      }
    }
  } catch {
    /* resposta não-JSON — ignora */
  }
  return out.slice(0, 4);
}

export function usePlanSuggestions(plan, blocks) {
  const heuristic = useMemo(() => computeSuggestions(blocks), [blocks]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [error, setError] = useState(null);

  const fetchAiSuggestions = useCallback(async () => {
    setLoadingAi(true);
    setError(null);
    const sys = [
      'Você é o copiloto de planejamento do Apolo Plan tab.',
      'Dado o canvas atual de um plano de sprint, sugira 3 PRÓXIMOS PASSOS concretos e específicos ao conteúdo (não genéricos).',
      'Responda APENAS com um array JSON dentro de um bloco ```json```, no formato:',
      '[{"label":"texto curto do chip (≤4 palavras)","kind":"text|heading|todo|skill|mermaid","payload":{...},"reason":"por que faz sentido agora"}]',
      'Regras de payload: kind "skill" → {"skill_name":"...","notes":"..."}; "heading" → {"text":"...","level":2}; "text" → {"body":"markdown"}; "todo" → {"items":[{"text":"...","done":false}]}.',
      '',
      `Plano: "${plan?.name || 'sem nome'}".`,
      `Canvas atual (${blocks?.length || 0} blocos):`,
      summarize(blocks),
    ].join('\n');

    const res = await apiFetch('/api/chat', {
      method: 'POST',
      body: {
        model: MODEL,
        system: sys,
        messages: [{ role: 'user', content: 'Sugira os 3 próximos passos.' }],
        max_tokens: 700,
      },
    });
    setLoadingAi(false);
    if (!res.ok) {
      setError(res.error || 'Falha ao buscar sugestões');
      return;
    }
    const data = res.data || {};
    const content = data?.content?.[0]?.text || data?.text || data?.message || '';
    const parsed = parseAiSuggestions(content);
    // Dedup contra heurística (por label, case-insensitive).
    const seen = new Set(heuristic.map((h) => h.label.toLowerCase()));
    setAiSuggestions(parsed.filter((p) => !seen.has(p.label.toLowerCase())));
  }, [plan, blocks, heuristic]);

  const suggestions = useMemo(() => [...heuristic, ...aiSuggestions], [heuristic, aiSuggestions]);

  return { suggestions, heuristic, aiSuggestions, fetchAiSuggestions, loadingAi, error };
}
